/**
 * Les routes poussées de l'espace tuteur que le câblage a pontées, rangées par lot.
 *
 * Le modèle est celui de l'élève (`student/studentSpaceRouteTypes.ts`) : une route de cette table rend
 * son écran legacy tant que son lot n'a rien inscrit pour elle dans son `routes.ts` ; une fois
 * inscrite, elle rend l'écran des ports — interrupteur `tutor_ds` allumé pour ce compte, un tuteur, et
 * hors live sauf si l'entrée déclare qu'elle vit en live (`livesInLive`). Le pont est posé une fois pour
 * toutes dans `navigationScreens.js` sur chacune de ces routes : un lot n'a qu'à remplir son `routes.ts`,
 * il ne touche ni la table de navigation ni le pont.
 *
 * Sur une route partagée avec l'élève (`account`, `settings`…), le pont du tuteur entoure celui de
 * l'élève : chacun ne bascule que son rôle (un compte tuteur ET élève garde l'espace de l'élève, qui a
 * sa coquille), le legacy reste celui des autres.
 *
 * **La candidature** (`application`) ne s'ouvre qu'au candidat (`tutor-candidate`, même aussi
 * `tutor`) : son registre est à part (`tutorCandidateRoutes`), et un candidat n'ouvre que lui — le
 * reste de l'espace (le compte compris) lui garde l'ancien écran, un tuteur n'ouvre jamais la
 * candidature. `intro` est partagée avec l'élève et le visiteur déconnecté, qui lisent l'interrupteur
 * éteint et gardent l'ancien écran.
 *
 * Le live (la salle) et le précall ont chacun leur lot (`room`, `preCall`). Une route de plus se pose ici
 * ET dans `navigationScreens.js` (`tutorSpaceRoutes.test.ts` vérifie les deux côtés).
 */
import {
    livesInLive,
    readRouteEntry,
    type StudentSpaceRouteEntry,
    type StudentSpaceScreen,
    type StudentSpaceScreenProps,
} from "../student/studentSpaceRouteTypes"

export const TUTOR_SPACE_ROUTES_BY_LOT = {
    // Ma dispo, libérer des créneaux, et une offre ouverte par son lien (mail, push ; `auth` compris).
    availability: ["tutor/ma-dispo", "tutor/liberer-creneaux", "group-attribution-proposals", "substitution-proposals"],
    // Ce qui entoure une séance, sauf la salle : la semaine (`tutor/lives`, une route neuve, l'ancienne
    // modale « Voir tous »), l'historique, gérer une séance, préparer, et les deux écrans d'après la
    // salle, poussés aussi dans la pile du live (la prescription, le bilan).
    lives: [
        "tutor/lives",
        "tutor-lives-history",
        "move-session",
        "tutor/prepare-live",
        "study-mission-prescription",
        "end-session-tutor",
    ],
    // Le compte : les pages que la feuille de profil ouvre, la formation, les vidéos, les factures,
    // compléter son profil. `account` et les trois suivantes sont partagées avec l'élève.
    // La salle du live : la route `menu` sous son titre `live` (le pont est posé sur ce titre seul ;
    // l'onglet `menu` hors live est la coquille). Son entrée vit en live (`livesInLive`), et une garde
    // la retient sur l'ancien écran dans une salle sans `events-panel` (`tutorSpaceRouteGuards`).
    room: ["menu"],
    // Le précall : `pre-call`, que « GO ! » pousse. Partagée avec l'élève (le pont du tuteur entoure le
    // sien), son entrée vit en live, comme celle de l'élève : c'est l'instant où le masteur entre.
    preCall: ["pre-call"],
    account: [
        "account",
        "settings",
        "account-password",
        "account-close",
        "validate-email",
        "edit-email",
        "tutor/formation",
        "videos-coaching",
        "tutoring-invoices",
        "tutor/complete-profile",
    ],
    // Mes élèves : la fiche d'un élève, et chacun des écrans que l'ancienne fiche poussait, repris par
    // un onglet de la nouvelle (ses activités, ses activités terminées, ses rapports, ses contrôles, la
    // conversation), et ses activités archivées. Sauf `student-profil`, toutes sont partagées avec
    // l'élève ou le parent : le pont du tuteur entoure le leur, chacun ne bascule que son rôle.
    students: [
        "student-profil",
        "student",
        "realizations",
        "live-reports",
        "school-assessments",
        "messenger",
        "archived-activites",
    ],
    // La candidature : l'intro du champion d'une étape, le profil, les entretiens (écrits et vidéo),
    // l'enregistrement. Pour le candidat seulement (`tutorCandidateRoutes`).
    application: ["intro", "application-personal-infos", "application-interview", "video-recording"],
} as const satisfies Record<string, readonly string[]>

/**
 * Les onglets de la coquille, par lot : un onglet sans écran inscrit rend son écran legacy
 * (`tutorTabScreens.js`), dans la coquille du design system. L'accueil est câblé par le socle.
 */
export const TUTOR_SPACE_TABS_BY_LOT = {
    home: ["dashboard"],
    availability: ["proposals"],
    account: ["tutor-resources"],
    students: ["students"],
} as const satisfies Record<string, readonly string[]>

export type TutorSpaceLot = keyof typeof TUTOR_SPACE_ROUTES_BY_LOT

/** Le nom d'une route pontée — tous lots confondus. */
export type TutorSpaceRouteName = (typeof TUTOR_SPACE_ROUTES_BY_LOT)[TutorSpaceLot][number]

/** Un onglet de la coquille. */
export type TutorSpaceTabName = (typeof TUTOR_SPACE_TABS_BY_LOT)[keyof typeof TUTOR_SPACE_TABS_BY_LOT][number]

/** Ce qu'un écran des ports reçoit, l'entrée d'un registre : les mêmes formes que chez l'élève. */
export type TutorSpaceScreenProps = StudentSpaceScreenProps
export type TutorSpaceScreen = StudentSpaceScreen
export type TutorSpaceRouteEntry = StudentSpaceRouteEntry

/** La table d'un lot : route → entrée. Vide tant que le lot n'a rien câblé. */
export type TutorSpaceRouteTable<Lot extends TutorSpaceLot> = Partial<
    Record<(typeof TUTOR_SPACE_ROUTES_BY_LOT)[Lot][number], TutorSpaceRouteEntry>
>

type TabLot = keyof typeof TUTOR_SPACE_TABS_BY_LOT

/** Les routes de la candidature, que seul le candidat ouvre. */
export type TutorCandidateRouteName = (typeof TUTOR_SPACE_ROUTES_BY_LOT)["application"][number]

/** Les routes de la candidature : route → entrée (hors live seulement). */
export type TutorCandidateRouteTable = Partial<Record<TutorCandidateRouteName, TutorSpaceRouteEntry>>

/**
 * L'onglet du candidat : sa candidature à la place de l'accueil (`dashboard`). Ses autres onglets,
 * s'il en a les droits, gardent leur écran legacy.
 */
export type TutorCandidateTabTable = Partial<Record<"dashboard", TutorSpaceScreen>>

/** Les onglets d'un lot : onglet → écran des ports (un onglet ne vit jamais en live). */
export type TutorSpaceTabTable<Lot extends TabLot> = Partial<
    Record<(typeof TUTOR_SPACE_TABS_BY_LOT)[Lot][number], TutorSpaceScreen>
>

export { livesInLive, readRouteEntry }
